<script setup lang="ts">
import { computed } from 'vue';
import { SLOTS, type Inventory, type Artifact, type EquipSlot } from '../game/inventory-api';
import { itemThumb } from '../game/art';
import { RARITY_HEX, SLOT_LABELS, durabilityPct } from '../game/item-math';
import BuySlots from './BuySlots.vue';
import SwipeDeck from './SwipeDeck.vue';

/**
 * THE STASH — the middle column of the Hero page. Purely presentational: the
 * inventory snapshot is owned by HeroScreen (single source of truth) and
 * handed down as a prop; this component only renders the filterable item grid
 * and asks the parent to open the shared ItemModal for a given item.
 *
 * NOTE: gear mutations (equip/upgrade/repair/sell) now live entirely in the
 * shared <ItemModal> (owned by HeroScreen) so one modal instance serves the
 * stash grid, the hero-card equipped strip AND the dossier paperdoll. The
 * `updated` event stays declared here for interface stability even though this
 * component no longer performs mutations directly.
 */
// `filter` is a controlled prop (v-model:filter from HeroScreen) so clicking an
// empty equipped-strip slot on the hero card can drive this grid's filter too.
const props = defineProps<{ inventory: Inventory; filter: EquipSlot | 'ALL' }>();
const emit = defineEmits<{
  (e: 'inspect', artifact: Artifact, slot: EquipSlot): void;
  (e: 'update:filter', filter: EquipSlot | 'ALL'): void;
  (e: 'updated'): void;
}>();
void emit; // 'updated' kept declared for API compatibility — see NOTE above

const FILTER_TABS: { key: EquipSlot | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  ...SLOTS.map((s) => ({ key: s, label: SLOT_LABELS[s] })),
];

function slotOf(a: Artifact): EquipSlot {
  return a.type.toLowerCase() as EquipSlot;
}

/** Equipped items first (so their gold "E" badge reads naturally near the top), then the bag. */
const allItems = computed<Artifact[]>(() => {
  const equipped = SLOTS.map((s) => props.inventory.equipped[s]).filter((a): a is Artifact => !!a);
  return [...equipped, ...props.inventory.bag];
});

function isEquipped(a: Artifact): boolean {
  return props.inventory.equipped[slotOf(a)]?.id === a.id;
}
const equippedIds = computed(() => new Set(allItems.value.filter((a) => isEquipped(a)).map((a) => a.id)));

const filtered = computed(() =>
  props.filter === 'ALL' ? allItems.value : allItems.value.filter((a) => slotOf(a) === props.filter));

function open(a: Artifact) {
  emit('inspect', a, slotOf(a));
}
</script>

<template>
  <section class="flex flex-col min-h-0 flex-1">
    <div class="flex items-center justify-between gap-3 mb-3 flex-wrap shrink-0">
      <div class="flex items-center gap-2.5">
        <h2 class="font-heading text-xl text-gold leading-none">The Stash</h2>
        <span v-if="inventory.bagCap" class="font-label uppercase tracking-wider text-[10px] text-text-dim/70 leading-none">{{ inventory.bagCount }} / {{ inventory.bagCap }} slots</span>
        <BuySlots @updated="emit('updated')" />
      </div>
      <nav class="flex items-center gap-3 flex-wrap">
        <button
          v-for="tab in FILTER_TABS" :key="tab.key"
          @click="emit('update:filter', tab.key)"
          :class="['chip-deco', filter === tab.key && 'chip-deco-active']"
        >{{ tab.label }}</button>
      </nav>
    </div>

    <p v-if="allItems.length === 0" class="font-body text-text-dim text-sm">Nothing stashed yet. Fights and heists will fill this out.</p>

    <!-- the stash scrolls inside its panel on desktop; on mobile it becomes a
         2-column, 6-per-page swipe deck (the page never scrolls sideways) -->
    <div v-else class="stash-scroll">
      <SwipeDeck
        :items="filtered" :page-size="6"
        min-col="120px" gap="0.65rem" :min-cells="8" pad-page
        :item-key="(a: any) => a.id"
      >
        <template #default="{ item: a }">
        <button
          class="stash-card"
          :class="{ 'stash-legendary': a.rarity === 'LEGENDARY', 'stash-broken': a.isBroken }"
          :style="{ '--rar': RARITY_HEX[a.rarity] }"
          @click="open(a)"
        >
          <span v-if="equippedIds.has(a.id)" class="stash-e-badge">E</span>
          <span class="stash-lv">Lv {{ a.level }}</span>
          <img :src="itemThumb(a.type, a.rarity, a.name)" :alt="a.type" class="stash-art" />
          <span class="stash-name">{{ a.name }}</span>
          <span v-if="a.isBroken" class="stash-broken-stamp">Broken</span>
          <div v-if="a.isBreakable && a.maxDurability != null" class="stash-wear">
            <div class="stash-wear-fill" :style="{ width: durabilityPct(a) + '%', background: durabilityPct(a) > 50 ? 'var(--color-success)' : durabilityPct(a) > 25 ? '#d9a441' : 'var(--color-error)' }"></div>
          </div>
        </button>
        </template>
      </SwipeDeck>
    </div>
  </section>
</template>

<style scoped>
.stash-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-gold) 35%, transparent) transparent;
  /* top room so the hover lift (translateY(-2px)) never clips the first row's border */
  padding: 3px 4px 3px 0;
}
.stash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.65rem;
}
.stash-card {
  position: relative;
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 0.7rem 0.5rem 0.55rem;
  border-radius: 9px;
  border: 1px solid var(--color-border, #2a2320);
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--rar, #9a927f) 10%, transparent), rgba(0,0,0,0.16)),
    var(--color-surface, #17131f);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  cursor: pointer;
  transition: transform 130ms ease, box-shadow 130ms ease, border-color 130ms ease;
  text-align: center;
}
.stash-card:hover {
  transform: translateY(-2px);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 6px 16px rgba(0,0,0,0.4);
}
.stash-legendary { animation: stash-pulse 2.2s ease-in-out infinite; }
@keyframes stash-pulse {
  0%, 100% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.03), 0 0 10px color-mix(in srgb, var(--rar) 25%, transparent); }
  50% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.03), 0 0 22px color-mix(in srgb, var(--rar) 65%, transparent); }
}
.stash-broken { filter: saturate(0.35) brightness(0.75); }
.stash-art { width: 44px; height: 44px; object-fit: cover; border-radius: 7px; }
.stash-name {
  font-family: var(--font-heading, serif);
  color: var(--rar, #ece6da);
  font-size: 12.5px; line-height: 1.2;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.stash-lv {
  position: absolute; top: 5px; left: 6px;
  font-family: var(--font-label, sans-serif); font-size: 9px;
  color: var(--color-text-dim, #8a8378);
  background: rgba(0,0,0,0.5); border-radius: 999px; padding: 1px 6px;
}
.stash-e-badge {
  position: absolute; top: 4px; right: 5px;
  display: grid; place-items: center;
  width: 16px; height: 16px; border-radius: 999px;
  font-family: var(--font-label, sans-serif); font-size: 10px; font-weight: 700;
  color: var(--color-bg, #0a0714); background: var(--color-gold, #c9a84c);
  box-shadow: 0 0 6px color-mix(in srgb, var(--color-gold, #c9a84c) 70%, transparent);
}
.stash-broken-stamp {
  font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.1em;
  font-size: 9px; color: var(--color-error, #b3263a);
  border: 1px solid color-mix(in srgb, var(--color-error, #b3263a) 60%, transparent);
  border-radius: 999px; padding: 1px 7px;
}
.stash-wear { width: 100%; height: 4px; border-radius: 999px; background: rgba(0,0,0,0.5); overflow: hidden; margin-top: 2px; }
.stash-wear-fill { height: 100%; border-radius: 999px; transition: width 400ms ease-out; }
.stash-empty-cell {
  border: 1px dashed var(--color-border, #2a2436);
  border-radius: 9px;
  min-height: 108px;
  background: rgba(0,0,0,0.12);
}

@media (prefers-reduced-motion: reduce) {
  .stash-legendary { animation: none; }
}
</style>
